// src/components/Shared/NotificationToast.jsx
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export const getPageNotificationTheme = (pathname) => {
  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const cleanPath = path.replace(/^\/en/, '') || '/';

  if (cleanPath === '/rcon-audit' || cleanPath.startsWith('/rcon')) {
    return { bg: '#c026d3', text: '#000000', shadow: 'rgba(192, 38, 211, 0.45)' };
  }
  if (cleanPath === '/server-data-extraction') {
    return { bg: '#14b8a6', text: '#000000', shadow: 'rgba(20, 184, 166, 0.45)' };
  }
  if (cleanPath === '/account-checker') {
    return { bg: '#1bd96a', text: '#000000', shadow: 'rgba(27, 217, 106, 0.45)' };
  }
  if (cleanPath === '/bots') {
    return { bg: '#3b82f6', text: '#000000', shadow: 'rgba(59, 130, 246, 0.45)' };
  }
  if (cleanPath === '/ddos') {
    return { bg: '#ef4444', text: '#000000', shadow: 'rgba(239, 68, 68, 0.45)' };
  }
  if (cleanPath === '/servers') {
    return { bg: '#0ea5e9', text: '#000000', shadow: 'rgba(14, 165, 233, 0.45)' };
  }
  if (cleanPath === '/monitoring-parser' || cleanPath === '/servers-parser') {
    return { bg: '#f97316', text: '#000000', shadow: 'rgba(249, 115, 22, 0.45)' };
  }
  if (cleanPath === '/secret') {
    return { bg: '#ffffff', text: '#000000', shadow: 'rgba(255, 255, 255, 0.4)' };
  }

  // Default: home, account, terms, login, etc.
  return { bg: '#ffffff', text: '#000000', shadow: 'rgba(255, 255, 255, 0.4)' };
};

const NotificationToast = ({ toast }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || typeof document === 'undefined') return null;

  const isVisible = Boolean(toast?.show && toast?.message);
  const isError = toast?.type === 'error';
  const isWarning = toast?.type === 'warning';

  // Determine theme: custom override or dynamic page theme
  const pageTheme = getPageNotificationTheme(toast?.pathname);
  const theme = isError
    ? { bg: '#ef4444', text: '#000000', shadow: 'rgba(239, 68, 68, 0.5)' }
    : (toast?.theme || pageTheme);

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-[calc(env(safe-area-inset-bottom,0px)+18px)] sm:bottom-[30px] left-1/2 z-[99999] pointer-events-none select-none"
      style={{
        transform: isVisible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(100px)',
        opacity: isVisible ? 1 : 0,
        transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease'
      }}
    >
      <div
        className="rounded-full px-4 py-2 sm:px-6 sm:py-3 flex items-center gap-2 sm:gap-2.5 font-sans font-bold text-[0.82rem] sm:text-[0.9rem] whitespace-nowrap max-w-[calc(100vw-24px)] overflow-hidden"
        style={{
          backgroundColor: theme.bg,
          color: theme.text,
          boxShadow: `0 14px 40px rgba(0, 0, 0, 0.75), 0 0 32px ${theme.shadow}`
        }}
      >
        {isError ? (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-4 h-4 sm:w-[18px] sm:h-[18px] shrink-0"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        ) : isWarning ? (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-4 h-4 sm:w-[18px] sm:h-[18px] shrink-0"
          >
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        ) : (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-4 h-4 sm:w-[18px] sm:h-[18px] shrink-0"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
        <span className="truncate tracking-tight">{toast?.message}</span>
      </div>
    </div>,
    document.body
  );
};

export default NotificationToast;
